import { Archive, ArchiveRestore, Star, Trash2, Ban } from "lucide-react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import type { Conversation } from "./types"
import { AvatarPhoto } from "./avatar-photo"
import { useAuthStore } from "@/features/auth/store/auth.store"

interface ArchivedChatsPageProps {
  archivedChats: Conversation[]
  onUnarchive: (id: string) => void
  onDelete: (id: string) => void
  onFavorite: (id: string) => void
  onBlock: (id: string) => void
  onSelectChat: (chat: Conversation) => void
  onClose?: () => void
}

export function ArchivedChatsPage({
  archivedChats,
  onUnarchive,
  onDelete,
  onFavorite,
  onBlock,
  onSelectChat,
  onClose,
}: ArchivedChatsPageProps) {
  const t = useTranslation()
  const myUserId = useAuthStore((s) => s.presenceId)
  return (
    <div className="flex flex-col h-full bg-card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b border-border shrink-0">
        {onClose && (
          <button
            onClick={onClose}
            className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
        )}
        <h2 className="text-base font-semibold text-foreground">{t("presence.archivedChats")}</h2>
      </div>

      {/* Chat list */}
      <div className="flex-1 overflow-y-auto">
        {archivedChats.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center px-8 py-16">
            <Archive className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <p className="text-sm text-muted-foreground">{t("presence.noArchivedChats")}</p>
          </div>
        ) : (
          archivedChats.map(chat => {
            // The other person in a DM, so the row can ask the directory for
            // their CURRENT picture instead of trusting `chat.imageUrl` (a
            // copy taken when the conversation was created). A group has no
            // single peer.
            const peerId = !chat.isGroup ? chat.participants?.find(p => p.id !== myUserId)?.id : undefined
            return (
            <div
              key={chat.id}
              onClick={() => onSelectChat(chat)}
              className="flex items-center gap-3 px-4 py-3 hover:bg-muted/50 cursor-pointer transition-colors"
            >
              <div className="w-12 h-12 rounded-full bg-muted overflow-hidden shrink-0">
                <AvatarPhoto
                  src={chat.imageUrl}
                  alt={chat.name}
                  className="w-full h-full object-cover"
                  fallback={
                    <div className="w-full h-full flex items-center justify-center text-sm font-semibold text-muted-foreground bg-primary/10 text-primary">
                      {chat.name[0]?.toUpperCase() || "?"}
                    </div>
                  }
                  contactId={peerId}
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-foreground truncate">{chat.name}</p>
                <p className="text-xs text-muted-foreground truncate">{chat.lastMessage}</p>
              </div>
              <div className="flex items-center gap-1">
                <button
                  onClick={e => { e.stopPropagation(); onUnarchive(chat.id) }}
                  className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  title={t("presence.unarchive")}
                >
                  <ArchiveRestore className="w-4 h-4" />
                </button>
                <button
                  onClick={e => { e.stopPropagation(); onFavorite(chat.id) }}
                  className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                  title={t("presence.favorite")}
                >
                  <Star className="w-4 h-4" />
                </button>
                {/* Blocking is a thing you do to a person. A group is not one:
                    there is nobody to block, and offering it implies the group
                    can be silenced the way a contact can. */}
                {!chat.isGroup && (
                  <button
                    onClick={e => { e.stopPropagation(); onBlock(chat.id) }}
                    className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                    title={t("presence.blockAction")}
                  >
                    <Ban className="w-4 h-4" />
                  </button>
                )}
                <button
                  onClick={e => { e.stopPropagation(); onDelete(chat.id) }}
                  className="p-2 rounded-full hover:bg-muted text-destructive hover:bg-destructive/10 transition-colors"
                  title={t("common.delete")}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
            )
          })
        )}
      </div>
    </div>
  )
}
